import {useAccessToken, useIsUnitComplianceUser, useIsUnitUser} from "../../services/auth"
import React, {useEffect, useState} from "react"
import TitleBar, {TitleBarAction} from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import HorizontalField, {optionalHorizontalField} from "../../components/HorizontalField/HorizontalField"
import {
	BusinessWalletCustomer,
	CustomerStatus,
	CustomerType,
	CustomerTypeIcons,
	updateBusinessWalletCustomer,
	updateCustomerTags,
} from "../../resources/customer"
import Card from "../../containers/Card/Card"
import TagsCard from "../../components/Tags/TagsCard"
import {Transactions} from "../../components/Transactions/Transactions"
import {TransactionsColumns} from "../../components/Transactions/Transactions.types"
import {UnitUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {useToasts} from "react-toast-notifications"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {
	Editable,
	EditableAddress,
	EditableHiddenString,
	EditableString,
	OptionalEditable,
} from "../../components/HorizontalField/EditableHorizontalField"
import {isUndefined, startCase} from "lodash"
import {hasPermission} from "../../services/permission"
import CustomerKnownNamesComponent from "../../components/CustomerKnownNames/CustomerKnownNames"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {CustomerKnownNames, getCustomerKnownNames} from "../../resources/customerKnownNames"
import CustomerCounterparties from "../../components/CustomerCounterParties/CustomerCounterparties"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import {useModal} from "react-modal-hook"
import {ArchiveCustomerModal} from "../Customer/ArchiveCustomerModal"
import {UnarchiveCustomerModal} from "../Customer/UnarchiveCustomerModal"
import {Disputes} from "../../components/Disputes/Disputes"
import {DisputesColumns} from "../../components/Disputes/Disputes.types"
import {Accounts} from "../../components/Accounts/Accounts"
import {ActionButton, ActionItem} from "../../components/ActionButton/ActionButton"
import {isOrg, Org} from "../../resources/org"
import {SpecificCustomerProps} from "../Customer/Customer"
import {useAuditLog} from "../../components/AuditLogDrawer/useAuditLog"
import {getAuditLogEventsHeaderMetaItems} from "../Customer/auditLogMetaItems"
import AuditLogActionButton from "../../components/AuditLogActionButton/AuditLogActionButton"
import AuditLogDrawer from "../../components/AuditLogDrawer/AuditLogDrawer"
import {OneProgramAdmonition} from "../../components/OneProgranAdmonition/OneProgramAdmonition"
import ReceivedPayments, {ReceivedPaymentsColumns} from "../../components/ReceivedPayments/ReceivedPayments"
import {AccountsColumns} from "../../components/Accounts/Accounts.types"
import {useListReturnPath} from "../../hooks/useListReturnPath"

export default function BusinessWalletPage({value: customer, refresh}: SpecificCustomerProps<BusinessWalletCustomer>) {
	const listReturnPath = useListReturnPath("/customers/")
	const accessToken = useAccessToken()
	const isUnitUser = useIsUnitUser()
	const {addToast} = useToasts()
	const businessWallet = customer.data
	const {attributes} = businessWallet
	const org = customer.included?.find(
		(r) => isOrg(r) && r.id == businessWallet.relationships.org.data.id
	) as unknown as Org
	const [isEditing, setIsEditing] = useState<string | undefined>(undefined)
	const [isUpdating, setIsUpdating] = useState(false)
	const [businessName, setBusinessName] = useState(attributes.businessName)
	const [address, setAddress] = useState(attributes.address)
	const [ein, setEin] = useState(attributes.ein)
	const [updateState, updateApp] = useAsyncResultIdle(updateBusinessWalletCustomer)
	const [refreshToken] = useRefreshToken()
	const isArchived = attributes.status == CustomerStatus.Archived
	const isCustomerEditable = !isArchived
	const isHiddenIconVisible = !useIsUnitComplianceUser()
	const [showArchiveCustomer, hideArchiveCustomer] = useModal(() => (
		<ArchiveCustomerModal closeModal={hideArchiveCustomer} customer={businessWallet} onSuccess={refresh} />
	))
	const [showUnarchiveCustomer, hideUnarchiveCustomer] = useModal(() => (
		<UnarchiveCustomerModal closeModal={hideUnarchiveCustomer} customer={businessWallet} onSuccess={refresh} />
	))
	const customerKnownNamesResult = isUnitUser
		? useAsyncResult<OkDocument<CustomerKnownNames>, ErrorDocument>(
				() => getCustomerKnownNames(accessToken, businessWallet.id),
				[refreshToken]
		  )
		: null

	useEffect(() => {
		if (updateState.isOk()) {
			addToast("Customer Updated Successfully", {appearance: "success"})
			setIsUpdating(false)
			refresh()
		} else if (updateState.isErr()) {
			addToast(updateState.error.errors[0].detail || updateState.error.errors[0].title, {appearance: "error"})
			setIsUpdating(false)
			refresh()
		}
	}, [updateState])

	const customerType = CustomerType.businessWallet
	const customerTypeDisplayText = startCase(customerType)
	const customerTypeIcon = CustomerTypeIcons[customerType]
	const auditLogEventsHeaderMetaItems = getAuditLogEventsHeaderMetaItems({
		customerName: businessName,
		customerType: customerTypeDisplayText,
		pageIcon: customerTypeIcon,
		org: org as Org,
	})
	const {showAuditLog, openAuditLogDrawer, closeAuditLogDrawer, isAuditLogDrawerOpened} = useAuditLog()
	return (
		<>
			<TitleBar
				title={businessName}
				breadcrumbs={[{text: "Customers", path: listReturnPath}]}
				meta={[{text: customerTypeDisplayText, icon: customerTypeIcon}]}
				status={isArchived ? {text: "Archived", color: "#B0B2B5", backgroundColor: "#EBECEE"} : undefined}
			>
				{showAuditLog && <AuditLogActionButton onClick={openAuditLogDrawer} />}
				{attributes.status != CustomerStatus.Archived ? (
					<HasPermission resource="customer.status" action="update">
						<TitleBarAction>
							<ActionButton enableActions={true}>
								<ActionItem
									title={"Archive customer"}
									color={"#FF506F"}
									icon={"user-geometric-action-cross-remove"}
									onClick={showArchiveCustomer}
								/>
							</ActionButton>
						</TitleBarAction>
					</HasPermission>
				) : (
					<HasPermission resource="customer.status.unarchive" action="update">
						<TitleBarAction>
							<ActionButton enableActions={true}>
								<ActionItem
									title={"Unarchive customer"}
									color={"#FF506F"}
									icon={"user-geometric-action-cross-remove"}
									onClick={showUnarchiveCustomer}
								/>
							</ActionButton>
						</TitleBarAction>
					</HasPermission>
				)}
			</TitleBar>
			{org?.relationships?.parentOrg && <OneProgramAdmonition />}

			<MainSection>
				<AuditLogDrawer
					open={isAuditLogDrawerOpened}
					onClose={closeAuditLogDrawer}
					auditLogEventsHeaderMetaItems={auditLogEventsHeaderMetaItems}
					resourceType="customer"
					showSubResourcesId={true}
					resourceId={businessWallet.id}
				/>
				<div className="columns">
					<div className="column is-4">
						<div className="columns">
							<div className="column">
								<div className="card">
									<div className="card-header">
										<p className="card-header-title">Contact</p>
									</div>
									<div className="card-content">
										<UnitUserOnly>
											{optionalHorizontalField(
												"Org",
												org && org.type == "org" ? org.attributes.name : undefined,
												undefined,
												isArchived ? "archived-field" : ""
											)}
										</UnitUserOnly>
									</div>
								</div>
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<div className="card">
									<div className="card-header">
										<p className="card-header-title">Business Information</p>
									</div>
									<div className="card-content">
										<HorizontalField label="Customer Id" fieldClassName={isArchived ? "archived-field" : ""}>
											<input type="text" className="input is-static" readOnly value={businessWallet.id} />
										</HorizontalField>
										<Editable
											label="Business Name"
											isEditing={isEditing === "Business Name"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && isUnitUser}
											isUpdating={isUpdating}
											onStartEdit={() => setIsEditing("Business Name")}
											onCancel={refresh}
											setValue={setBusinessName}
											value={businessName}
											initial={businessName}
											update={() => {
												updateApp(accessToken, businessWallet, {businessName: businessName})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										>
											{EditableString}
										</Editable>
										<OptionalEditable
											label="EIN"
											isEditing={isEditing === "EIN"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && isUnitUser}
											isUpdating={isUpdating}
											isHiddenIconVisible={isHiddenIconVisible}
											onStartEdit={() => setIsEditing("EIN")}
											onCancel={refresh}
											setValue={setEin}
											value={ein}
											initial={ein}
											update={() => {
												updateApp(accessToken, businessWallet, {ein: ein})
												setIsUpdating(true)
											}}
											pattern="^\d{9}$"
											className={isArchived ? "archived-field" : ""}
										>
											{EditableHiddenString}
										</OptionalEditable>
										<OptionalEditable
											label="Address"
											isEditing={isEditing === "Address"}
											isDisabled={!isUndefined(isEditing)}
											isAllowed={isCustomerEditable && hasPermission("customer", "update")}
											isUpdating={isUpdating}
											onStartEdit={() => setIsEditing("Address")}
											onCancel={refresh}
											setValue={setAddress}
											value={address}
											initial={address}
											update={(v) => {
												updateApp(accessToken, businessWallet, {address: v})
												setIsUpdating(true)
											}}
											className={isArchived ? "archived-field" : ""}
										>
											{EditableAddress}
										</OptionalEditable>
									</div>
								</div>
							</div>
						</div>

						<Card title="Counterparties">
							<CustomerCounterparties customerId={businessWallet.id} limit={100} isArchived={isArchived} />
						</Card>

						<div className="columns">
							<div className="column">
								<TagsCard
									tags={attributes.tags}
									onUpdateTags={(token, tags) =>
										updateCustomerTags(token, businessWallet.id, businessWallet.type, tags)
									}
									refresh={refresh}
									editable={hasPermission("customer", "update")}
								/>
							</div>
						</div>
						{customerKnownNamesResult?.match(
							() => null,
							(aliases) => (
								<div className="columns">
									<div className="column">
										<div className="card">
											<div className="card-header">
												<p className="card-header-title">Known Aliases</p>
											</div>
											<div className="card-content">
												<CustomerKnownNamesComponent
													customerId={businessWallet.id}
													names={aliases.data.attributes.names}
													refresh={refresh}
													fieldClassName={"archived-field"}
												/>
											</div>
										</div>
									</div>
								</div>
							),
							() => null
						)}
					</div>
					<div className="column">
						<Accounts
							customerId={businessWallet.id}
							enableTitle
							fullHeight={false}
							includedColumns={[
								AccountsColumns.id,
								...(isUnitUser ? [AccountsColumns.bank] : []),
								AccountsColumns.balance,
								AccountsColumns.type,
								AccountsColumns.product,
								AccountsColumns.status,
								AccountsColumns.routingNumber,
								AccountsColumns.accountNumber,
								AccountsColumns.purpose,
								AccountsColumns.createdAt,
							]}
						/>

						<Transactions
							fullHeight={false}
							customerId={businessWallet.id}
							limit={10}
							isUsingPaging={true}
							includedColumns={[
								TransactionsColumns.id,
								TransactionsColumns.type,
								TransactionsColumns.amount,
								TransactionsColumns.balance,
								TransactionsColumns.summary,
								TransactionsColumns.createdAt,
							]}
						/>

						<ReceivedPayments
							fullHeight={false}
							enableTitle={true}
							enableSearchBox={false}
							disableDrawer={true}
							customerId={businessWallet.id}
							includedColumns={[
								ReceivedPaymentsColumns.id,
								ReceivedPaymentsColumns.type,
								ReceivedPaymentsColumns.direction,
								ReceivedPaymentsColumns.companyName,
								ReceivedPaymentsColumns.amount,
								ReceivedPaymentsColumns.status,
								ReceivedPaymentsColumns.createdAt,
								ReceivedPaymentsColumns.completionDate,
							]}
						/>

						<Disputes
							enableTitle={true}
							fullHeight={false}
							limit={10}
							customerId={businessWallet.id}
							includedColumns={[
								DisputesColumns.id,
								DisputesColumns.source,
								DisputesColumns.amount,
								DisputesColumns.createdAt,
								DisputesColumns.status,
								DisputesColumns.updatedAt,
							]}
						/>
					</div>
				</div>
			</MainSection>
		</>
	)
}
